import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * ErrorBoundary — Production-grade React error boundary for PathKit.
 *
 * PURPOSE:
 * Catches unhandled render/runtime errors in child component trees and
 * displays a safe fallback UI instead of crashing the entire app to a
 * white screen.
 *
 * USAGE:
 *   <ErrorBoundary name="ConsultantDashboard">
 *     <ConsultantDashboard ... />
 *   </ErrorBoundary>
 *
 * The optional `name` prop is logged alongside the error for faster triage.
 *
 * CREATED: 2026-02-11 — MVP Production Hardening (Blocker #2)
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Human-readable label for log attribution (e.g. "ConsultantDashboard") */
  name?: string;
  /** Optional custom fallback — defaults to built-in recovery UI */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console — intentionally NOT swallowed.
    // In production, wire this to an external service (Sentry, etc.) if desired.
    const label = this.props.name || 'Unknown';
    console.error(
      `[ErrorBoundary:${label}] Caught render error:`,
      error,
    );
    console.error(
      `[ErrorBoundary:${label}] Component stack:`,
      errorInfo.componentStack,
    );
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Allow callers to supply a custom fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default safe fallback UI — neutral, non-alarming
      return (
        <div className="flex min-h-[300px] w-full items-center justify-center p-8">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h2 className="mb-2 text-[1.125rem] font-semibold leading-snug text-gray-900">
              Something went wrong
            </h2>
            <p className="mb-6 text-[0.875rem] leading-relaxed text-gray-500">
              An unexpected error occurred. Please refresh the page to continue.
            </p>

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-[0.875rem] font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              >
                Try Again
              </button>
              <button
                onClick={this.handleRefresh}
                className="rounded-md bg-blue-600 px-4 py-2 text-[0.875rem] font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
              >
                Refresh Page
              </button>
            </div>

            {/* Show error detail in non-production for developer debugging */}
            {this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-[0.75rem] text-gray-400 hover:text-gray-600">
                  Technical details
                </summary>
                <pre className="mt-2 max-h-32 overflow-auto rounded bg-gray-50 p-3 text-[0.6875rem] leading-snug text-gray-600">
                  {this.state.error.message}
                  {this.state.error.stack &&
                    '\n\n' + this.state.error.stack.slice(0, 500)}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
